import { useTranslation } from '@fluentsms/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useAuth } from '..';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { renderInlineBoldedWordText } from '../helpers/renderInlineBoldedWordText';
import { useStyles } from './styles';
// eslint-disable-next-line no-restricted-imports
import { Grid, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

type IdlePrompterMessageType = 'active' | 'idle' | 'prompt';

const promptBeforeIdle = 120_000; // two minutes in MS
const idTokenRefreshTime = 180_000; // three minutes in MS
const throttleTime = 500; // 500ms

const IdlePrompter: React.FC = () => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const classes = useStyles();
  const { tokenUtility, renewTokens } = useAuth();
  const exp = tokenUtility.getMinimumExpiration();
  const iat = tokenUtility.getMaximumIssuedAt();
  const expirationMilliseconds = exp * 1000;
  const issuedAtMilliseconds = iat * 1000;
  const timeout = expirationMilliseconds - issuedAtMilliseconds;
  const [remaining, setRemaining] = useState<number>(timeout);
  const [isInactiveDialogueOpen, setIsInactiveDialogueOpen] = useState<boolean>(false);
  const [isRenewingSession, setIsRenewingSession] = useState<boolean>(false);
  const interval = useRef<NodeJS.Timer | null>(null);

  const handleLogout = useCallback((): void => {
    history.push('/logout');
  }, [history]);

  const onActive = (): void => {
    setIsInactiveDialogueOpen(false);
  };

  const onIdle = (): void => {
    setIsInactiveDialogueOpen(false);
    handleLogout();
  };

  const onPrompt = (): void => {
    setIsInactiveDialogueOpen(true);
  };

  /**
   * Invokes the passed in function and sends a message to other instances of IdlePrompter (such as a new tab).
   * It's important to wrap the function with this method before passing it to `useIdleTimer` to avoid circular dependencies.
   * @param messageType The message type to send.
   * @param fn The function to invoke.
   * @returns A function that invokes the passed in function and sends a message to other instances of IdlePrompter.
   */
  const withMessage = (messageType: IdlePrompterMessageType, fn: () => void): (() => void) => {
    return () => {
      fn();
      message(messageType);
    };
  };

  const onMessage = (messageType: IdlePrompterMessageType): void => {
    // the `| undefined` is a bit of defensive programming in case the definition of `IdlePrompterMessageType` is not exhaustive.
    const fnMap: Record<IdlePrompterMessageType, (() => void) | undefined> = {
      active: onActive,
      idle: onIdle,
      prompt: onPrompt,
    };

    fnMap[messageType]?.();
  };

  const checkSession = useCallback(
    (callback?: () => void) => {
      setIsRenewingSession(true);
      renewTokens({
        onSuccess: (response) => {
          tokenUtility.setTokens(response);
          callback?.();
          setIsRenewingSession(false);
        },
        onError: (error) => {
          console.error('Token cannot be renewed.', error);
          handleLogout();
        },
      });
    },
    [handleLogout, renewTokens, tokenUtility],
  );

  /**
   * If the difference between the expiration time and the current time is less than
   * or equal to expirationTimeToCheckInMs, then it's expiring within the next expirationTimeToCheckInMs
   */
  const isExpiringIn = (expirationTimeInMs: number, expirationTimeToCheckInMs: number): boolean => {
    const currentTimeInMs = Date.now();
    return expirationTimeInMs - currentTimeInMs <= expirationTimeToCheckInMs;
  };

  /**
   * Check to see if the token is expiring in less than two minutes.
   */
  const isExpiringSoon = useCallback((): boolean => {
    return isExpiringIn(expirationMilliseconds, idTokenRefreshTime);
  }, [expirationMilliseconds]);

  /**
   * If the token is soon to expire, automatically renew the session to get a fresh token.
   * This is to prevent the user from being logged out due to an expired token.
   * However, the token is not automatically renewed if the inactive dialogue is open or the session is already being renewed.
   */
  const checkSessionIfExpiringSoon = useCallback((): void => {
    if (isExpiringSoon() && !isInactiveDialogueOpen && !isRenewingSession) checkSession();
  }, [checkSession, isExpiringSoon, isInactiveDialogueOpen, isRenewingSession]);

  const { getRemainingTime, activate, message } = useIdleTimer({
    onIdle: withMessage('idle', onIdle),
    onActive: withMessage('active', onActive),
    onPrompt: withMessage('prompt', onPrompt),
    onMessage,
    timeout,
    promptBeforeIdle,
    throttle: throttleTime,
    crossTab: true,
    leaderElection: true,
    syncTimers: throttleTime,
    events: ['mousedown', 'keydown'], // we do not need to listen to ALL the events, as they can unintentionally trigger a false "active". Clicks and typing are enough.
  });

  useEffect(() => {
    if (!interval.current) {
      interval.current = setInterval(() => {
        setRemaining(Math.ceil(getRemainingTime() / 1000));
        checkSessionIfExpiringSoon();
      }, throttleTime);
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
    };
  }, [checkSessionIfExpiringSoon, getRemainingTime]);

  const handleStillHere = (): void => {
    checkSession(activate);
    setIsInactiveDialogueOpen(false);
  };

  const minutes = Math.floor(remaining / 60);
  const seconds = (remaining % 60).toString().padStart(2, '0');

  return (
    <ConfirmationDialog
      open={isInactiveDialogueOpen}
      autoFocus
      dialogTitle={t('idleTimeout.dialogTitle')}
      dialogContent={
        <>
          <Grid className={classes.bodyContainer}>
            <Typography>
              {renderInlineBoldedWordText(t('idleTimeout.dialogText'), `${minutes}:${seconds}.`, '')}
            </Typography>
            <br />
            <Typography>{t('idleTimeout.continuePrompt')}</Typography>
          </Grid>
        </>
      }
      primaryActionLabel={t('idleTimeout.dialogSubmitButton')}
      secondaryActionLabel={t('idleTimeout.dialogLogoutButton')}
      onClose={handleLogout}
      onConfirm={handleStillHere}
      disableBackdropClick
      disableEscapeKeyDown
    />
  );
};

export default IdlePrompter;
